import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Card,
  Link,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  CircularProgress,
} from '@mui/material';
import { Settings as SettingsIcon, Email as EmailIcon, ArrowBack, ArrowForward } from '@mui/icons-material';
import { Buffer } from 'buffer';

function Dashboard() {
  const [deals, setDeals] = useState([]);
  const [error, setError] = useState(null);
  const [openConversation, setOpenConversation] = useState(false);
  const [currentConversation, setCurrentConversation] = useState(null);
  const [currentConversationIndex, setCurrentConversationIndex] = useState(0);
  const [currentDealIndex, setCurrentDealIndex] = useState(0);
  const [isProcessingEmails, setIsProcessingEmails] = useState(false);

  useEffect(() => {
    fetchDeals();
  }, []);

  const fetchDeals = async () => {
    const token = localStorage.getItem('token');

    if (!token) {
      setError('User is not authenticated. Please log in.');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/deals?populate=conversations`, {
        headers: {
          'Authorization': `Token ${token}`,
          // 'ngrok-skip-browser-warning': 'true',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setDeals(data.results);
      } else if (response.status === 401) {
        setError('Unauthorized access. Please log in.');
      } else {
        setError('Failed to fetch deals');
      }
    } catch (error) {
      console.error('Error fetching deals:', error);
      setError('Error fetching deals.');
    }
  };

  const handleProcessEmails = async () => {
    const token = localStorage.getItem('token');
    setIsProcessingEmails(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/user/processEmails`, {
        method: 'POST',
        headers: {
          'Authorization': `Token ${token}`,
          // 'ngrok-skip-browser-warning': 'true',
        },
      });

      if (response.ok) {
        // Refresh deals after processing emails
        await fetchDeals();
      } else {
        setError('Failed to process emails');
      }
    } catch (error) {
      console.error('Error processing emails:', error);
      setError('Error processing emails.');
    } finally {
      setIsProcessingEmails(false);
    }
  };

  const handleViewConversations = (deal, index) => {
    setCurrentDealIndex(index);
    setCurrentConversationIndex(0);
    setCurrentConversation(deal.conversations[0]);
    setOpenConversation(true);
  };

  const handleTakeover = async (dealId) => {
    const token = localStorage.getItem('token');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/deal/${dealId}/takeover`, {
        method: 'POST',
        headers: {
          'Authorization': `Token ${token}`,
          // 'ngrok-skip-browser-warning': 'true',
        },
      });

      if (response.ok) {
        // Refresh deals after takeover
        fetchDeals();
      } else {
        setError('Failed to takeover deal');
      }
    } catch (error) {
      console.error('Error taking over deal:', error);
      setError('Error taking over deal.');
    }
  };

  const handleCloseConversation = () => {
    setOpenConversation(false);
  };

  const handleNextConversation = () => {
    if (currentConversationIndex < deals[currentDealIndex]?.conversations.length - 1) {
      setCurrentConversationIndex(currentConversationIndex + 1);
      setCurrentConversation(deals[currentDealIndex]?.conversations[currentConversationIndex + 1]);
    }
  };

  const handlePreviousConversation = () => {
    if (currentConversationIndex > 0) {
      setCurrentConversationIndex(currentConversationIndex - 1);
      setCurrentConversation(deals[currentDealIndex]?.conversations[currentConversationIndex - 1]);
    }
  };

  const decodeContent = (content) => {
    try {
      // First, try to decode from base64
      const decodedContent = Buffer.from(content, 'base64').toString('utf-8');
      
      // Replace \n with actual newlines and preserve other whitespace
      return decodedContent.replace(/\\n/g, '\n').replace(/\\r/g, '\r').replace(/\\t/g, '\t');
    } catch (error) {
      console.error('Error decoding content:', error);
      return content; // Return original content if decoding fails
    }
  };

  return (
    <Container maxWidth="2xl">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} mt={8}>
        <Typography variant="h4" component="h1">
          Negotiations
        </Typography>
        <Box>
          <Button
            startIcon={isProcessingEmails ? <CircularProgress size={20} color="inherit" /> : <EmailIcon />}
            variant="contained"
            color="primary"
            onClick={handleProcessEmails}
            disabled={isProcessingEmails}
            style={{ marginRight: '10px' }}
          >
            {isProcessingEmails ? 'Processing...' : 'Process Emails'}
          </Button>
          <IconButton component={Link} href="/settings" color="primary">
            <SettingsIcon />
          </IconButton>
        </Box>
      </Box>
      {error && <Typography color="error">{error}</Typography>}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Brand</TableCell>
              <TableCell>Point of Contact</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Date Created</TableCell>
              <TableCell>Last Updated</TableCell>
              <TableCell>Details</TableCell>
              <TableCell>Conversations</TableCell>
              <TableCell>Stop</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {deals.map((deal, index) => (
              <TableRow key={deal.id}>
                <TableCell>{deal?.brand}</TableCell>
                <TableCell>{deal.poc}</TableCell>
                <TableCell>{deal.status}</TableCell>
                <TableCell>{new Date(deal.created_at).toLocaleDateString()}</TableCell>
                <TableCell>{new Date(deal.updated_at).toLocaleDateString()}</TableCell>
                <TableCell>
                  <Typography variant='body2' style={{ whiteSpace: 'pre-line' }}>
                    {deal.details}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Button
                    variant="outlined"
                    onClick={() => handleViewConversations(deal, index)}
                  >
                    View Conversations
                  </Button>
                </TableCell>
                <TableCell>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => handleTakeover(deal.id)}
                    disabled={deal.status === 'client_took_control'}
                  >
                    Stop AI Replies
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog open={openConversation} onClose={handleCloseConversation} maxWidth="lg" fullWidth >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Brand - {deals[currentDealIndex]?.brand}</Typography>
            <Box>
              <IconButton onClick={handlePreviousConversation} disabled={currentConversationIndex === 0}>
                <ArrowBack />
              </IconButton>
              <IconButton onClick={handleNextConversation} disabled={currentConversationIndex === deals[currentDealIndex]?.conversations.length - 1}>
                <ArrowForward />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {currentConversation?.messages.map((message, index) => (
            <Card key={index} variant='outlined' sx={{
              mb:5,
              p: 2
            }}>
              <Typography variant="subtitle2">{"Channel: "}{message.channel}</Typography>
              <Typography variant="subtitle2">{"From: "}{message.sender}</Typography>
              <Typography variant="subtitle2">{"Timestamp: "}{new Date(message.timestamp).toLocaleDateString()}{" : "}{new Date(message.timestamp).toLocaleTimeString()}</Typography>
              <Typography variant="subtitle2">{"Attachments: "}{message.attachments.map((attachment) => (
                <Link href={attachment.url}>{attachment.filename}</Link>
              ))}</Typography>
              <Typography variant="body1" sx={{mt:4, whiteSpace: 'pre-wrap'}}>
                {decodeContent(message.content)}
              </Typography>
            </Card>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConversation} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default Dashboard;
